import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { useCustomList } from "store/selectors/bridge";
import {
    customListKeyForEntityType,
    entityTypeNoun,
    hasSingleEmailAction,
    hasSingleSmsAction,
    hasSingleTaskAction,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

const fieldDescriptions: Record<string, string> = {
    chosenCommunityId: "A chosen community is selected for the client",
    referralContactId: "A referral source is set for the client",
    moveTimelineActual: "A move date is set for the client",
    statusCheckLaterDate: "A check back later date is set for the client",
};

const discreteToDescriptions: Record<string, string> = {
    referredUnselectedCommunities:
        "email each unchosen community that received a client referral",
    allCommunityOptions: "email all community options",
};

const StatusCondition: React.FC<{
    statusIds: string[];
    attachTo: AdvancedSearchEntityType;
}> = ({ statusIds, attachTo }) => {
    const { getListItemName, status } = useCustomList(
        customListKeyForEntityType(attachTo),
    );
    const noun = entityTypeNoun(attachTo);

    if (status !== "ready") {
        return <li>The {noun}&apos;s status matches</li>;
    }

    const names = statusIds.map(id => getListItemName(id)).filter(Boolean);

    if (names.length === 0) {
        return null;
    }

    return (
        <li>
            The {noun}&apos;s status is one of:{" "}
            <span className="inline-flex flex-wrap gap-1">
                {names.map((name, i) => (
                    <span
                        key={i}
                        className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {name}
                    </span>
                ))}
            </span>
        </li>
    );
};

const useActionPrefix = (workflow: EditableWorkflow): string | null => {
    const action = workflow.actions?.[0];

    if (
        hasSingleEmailAction(workflow) &&
        action?.__typename === "WorkflowActionEmailEntity"
    ) {
        if (action.discreteTo) {
            const description = discreteToDescriptions[action.discreteTo];
            if (description) {
                return description;
            }
        }
        return "send an email";
    }

    if (hasSingleSmsAction(workflow)) {
        return "send a text message";
    }

    if (hasSingleTaskAction(workflow)) {
        return "create a task";
    }

    return null;
};

interface ConditionItem {
    key: string;
    element: React.ReactNode;
}

const useEntityUpdatedConditions = (
    workflow: EditableWorkflow,
): ConditionItem[] | null => {
    const fieldMatchers = workflow.trigger?.entityUpdated?.fieldMatchers || [];
    const statusMatcher = fieldMatchers.find(
        m =>
            (m.fieldName === "status" || m.fieldName === "statusListItem") &&
            Array.isArray(m.current?.values),
    );
    const nonStatusMatchers = fieldMatchers.filter(
        m => m.fieldName !== "status" && m.fieldName !== "statusListItem",
    );

    const conditions: ConditionItem[] = [];

    for (const matcher of nonStatusMatchers) {
        if (
            matcher.current?.specialValue === "$NOT_EMPTY" &&
            fieldDescriptions[matcher.fieldName]
        ) {
            conditions.push({
                key: matcher.fieldName,
                element: <li>{fieldDescriptions[matcher.fieldName]}</li>,
            });
        } else {
            return null;
        }
    }

    if (statusMatcher?.current?.values?.length > 0) {
        conditions.push({
            key: "status",
            element: (
                <StatusCondition
                    statusIds={statusMatcher.current.values}
                    attachTo={workflow.attachTo}
                />
            ),
        });
    }

    return conditions;
};

const EntityUpdatedConditions: React.FC<{
    workflow: EditableWorkflow;
}> = ({ workflow }) => {
    const actionPrefix = useActionPrefix(workflow);
    const conditions = useEntityUpdatedConditions(workflow);

    if (!conditions || conditions.length === 0) {
        return null;
    }

    return (
        <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">
            <p className="font-medium text-gray-900">
                This workflow will {actionPrefix || "run"} when:
            </p>
            <ul className="mt-2 ml-4 space-y-1 list-disc list-outside">
                {conditions.map(c => (
                    <React.Fragment key={c.key}>{c.element}</React.Fragment>
                ))}
            </ul>
        </div>
    );
};

const EmailOpenedConditions: React.FC<{
    workflow: EditableWorkflow;
}> = ({ workflow }) => {
    const actionPrefix = useActionPrefix(workflow);
    const trigger = workflow.trigger?.emailOpened;

    if (!trigger) {
        return null;
    }

    const thresholdDays = trigger.activityThresholdDays || 0;

    const statusMatcher = trigger.entityFieldMatchers?.find(
        m => m.fieldName === "statusListItem",
    );

    return (
        <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">
            <p className="font-medium text-gray-900">
                This workflow will {actionPrefix || "run"} when:
            </p>
            <ul className="mt-2 ml-4 space-y-1 list-disc list-outside">
                <li>
                    A {entityTypeNoun(workflow.attachTo)} email is opened
                    {thresholdDays > 0 && <> after {thresholdDays} days of inactivity</>}
                </li>
                {statusMatcher?.current?.values?.length > 0 ? (
                    <StatusCondition
                        statusIds={statusMatcher.current.values}
                        attachTo={workflow.attachTo}
                    />
                ) : null}
            </ul>
        </div>
    );
};

const InactiveClientConditions: React.FC<{
    workflow: EditableWorkflow;
}> = ({ workflow }) => {
    const actionPrefix = useActionPrefix(workflow);
    const entityCondition = workflow.trigger?.schedule?.entityCondition;

    if (!entityCondition || entityCondition.nodeType !== "And") {
        return null;
    }

    const statusCondition = entityCondition.children?.find(
        condition =>
            condition.nodeType === "Or" &&
            condition.children?.every(
                c => c.nodeType === "FieldCondition" && c.fieldName === "statusListItem",
            ),
    );

    const dateCondition = entityCondition.children?.find(
        condition =>
            condition.nodeType === "FieldCondition" &&
            condition.fieldName === "updatedAt" &&
            typeof condition.relativeDate?.value === "number",
    );

    if (!statusCondition || !dateCondition) {
        return null;
    }

    const statusIds = statusCondition.children
        .filter(c => c.nodeType === "FieldCondition" && c.fieldName === "statusListItem")
        .map(c => c.textValue);

    const days = Math.abs(dateCondition.relativeDate.value);

    return (
        <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">
            <p className="font-medium text-gray-900">
                This workflow will {actionPrefix || "run"} daily when:
            </p>
            <ul className="mt-2 ml-4 space-y-1 list-disc list-outside">
                {statusIds.length > 0 ? (
                    <StatusCondition statusIds={statusIds} attachTo={workflow.attachTo} />
                ) : null}
                <li>
                    The {entityTypeNoun(workflow.attachTo)} has had no activity for {days}{" "}
                    {days === 1 ? "day" : "days"}
                </li>
            </ul>
        </div>
    );
};

interface WorkflowConditionsSummaryProps {
    workflow: EditableWorkflow;
}

export const WorkflowConditionsSummary: React.FC<WorkflowConditionsSummaryProps> = ({
    workflow,
}) => {
    const triggerType = workflow.trigger?.eventType;

    if (triggerType === "EntityUpdated") {
        return <EntityUpdatedConditions workflow={workflow} />;
    }

    if (triggerType === "EmailOpened") {
        return <EmailOpenedConditions workflow={workflow} />;
    }

    if (triggerType === "Schedule") {
        return <InactiveClientConditions workflow={workflow} />;
    }

    return null;
};
